import React, { useEffect, useRef, useState } from 'react';
import Markdown from './Markdown';
import { useAuth } from '../AuthContext';
import { acceptAgreements, agreementsStatus, takePendingAcceptance } from '../data/agreements';
import './Clickwrap.css';

const reachedEnd = (el) => !!el && el.scrollHeight - el.scrollTop - el.clientHeight <= 12;

// The agreements in one scroller, stacked in order. Agree stays off until
// the reader reaches the bottom (a stack that fits without scrolling counts
// as read). onAgree receives the documents seen, in order.
export default function Clickwrap({ docs, onAgree, busy = false, error = '', intro }) {
  const [atEnd, setAtEnd] = useState(false);
  const scroller = useRef(null);
  const key = (docs || []).map((d) => d.id).join('|');

  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const check = () => setAtEnd(reachedEnd(el));
    check();
    const id = requestAnimationFrame(check);
    el.addEventListener('scroll', check);
    window.addEventListener('resize', check);
    return () => {
      cancelAnimationFrame(id);
      el.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [key]);

  if (!docs?.length) return null;

  return (
    <div className="cw">
      {intro && <p className="cs-help cw-intro">{intro}</p>}
      <p className="cw-progress">{docs.length} documents · {docs.map((d) => d.title).join(' · ')}</p>
      <div className="cw-card">
        <div className="cw-body" ref={scroller} tabIndex={0} role="region" aria-label="Agreements">
          {docs.map((doc) => (
            <section key={doc.id} className="cw-doc">
              <div className="cw-head">
                <strong>{doc.title}</strong>
                <span className="cs-help" style={{ margin: 0 }}>version {doc.version}</span>
              </div>
              <Markdown text={doc.body} className="md cw-md" />
            </section>
          ))}
        </div>
      </div>
      {error && <p className="cs-help cw-err" role="alert">{error}</p>}
      <div className="cw-foot">
        <button type="button" className="cs-btn cs-btn-primary" disabled={!atEnd || busy} onClick={() => atEnd && !busy && onAgree(docs)}>
          {busy ? 'Saving…' : atEnd ? 'Agree and continue' : 'Scroll to the end to agree'}
        </button>
      </div>
    </div>
  );
}

// Signed-in gate around the member shell. The workspace is not usable until
// the person has accepted the current terms + privacy policy; accounts that
// predate the clickwrap are exempt server-side (agreements.enforceFrom).
// Finishes a signup acceptance parked by Login (the account exists before
// this mounts) before asking the backend.
export function ClickwrapGate({ children }) {
  const { user } = useAuth();
  const member = user && !user.isAnonymous;
  const [state, setState] = useState({ loading: true, userOk: true, docs: [], error: '' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const pending = takePendingAcceptance();
      if (pending) await acceptAgreements(pending).catch((err) => { if (err.status !== 409) throw err; });
      const data = await agreementsStatus();
      setState({ loading: false, userOk: !!data.userOk, docs: data.agreements || [], error: '' });
    } catch (err) {
      // Backend unreachable: fail open for the shell (the API routes stay
      // protected by their own auth) rather than locking every member out.
      setState({ loading: false, userOk: true, docs: [], error: err.message });
    }
  };

  useEffect(() => { if (member) load(); }, [member, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!member) return children;
  if (state.loading) return <div className="cs-auth-wrap">Loading…</div>;
  if (state.userOk) return children;
  return (
    <div className="cs-auth-wrap">
      <div className="cw-gate">
        <div className="cs-brand" style={{ fontSize: 28, marginBottom: 6 }}>{process.env.REACT_APP_APP_NAME || 'fast.legal'}</div>
        <h2 style={{ fontSize: 22, marginTop: 0 }}>Before you continue</h2>
        <Clickwrap
          docs={state.docs}
          busy={busy}
          error={state.error}
          intro="Read to the end, then agree: the terms of service and privacy policy."
          onAgree={async (docs) => {
            setBusy(true);
            try {
              await acceptAgreements(docs.map((d) => d.id));
              await load();
            } catch (err) {
              setState((s) => ({ ...s, error: err.message }));
            } finally {
              setBusy(false);
            }
          }}
        />
      </div>
    </div>
  );
}
